import {FC, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState} from "react";
import HighCharts from "highcharts";
import HighchartsReact from 'highcharts-react-official'
import { Link, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";

import AuthSubHeader from "src/components/authSubHeader";
import FooterText from "src/components/footerText";
import TableFilters from "src/components/caseFilters";
import RenderNextStep from "./caseItemActions";
import RequestDateOfTreatment from "src/components/modals/requestDateOfTreatment";
import CancelTreatment from "src/components/modals/cancelTreatment";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { BAR_CHART } from "../../constants/highCharts";
import { ToastContext } from "src/context/toastContext";
import { ToastTypes } from "src/types";
import { AuthContext } from "src/context/authContext";
import { LoaderContext } from "src/context/loaderContext";

import { CaseListAPIResponseType, CaseListErrorResponse, CaseListingType, DownloadZipResponseType, QueryParamsState, QueryStateActionType, QueryStateActions } from "./types";
import {
  CASE_HEADER_LISTING_IS_IOVERA_PRACTICE,
  CASE_STATUS_LISTING,
} from "src/constants/cases";
import { initialState, queryParamReducer } from "./reducer";
import { TreatmentListErrorResponse, TreatmentStatusUpdateApiResponse } from "../treatmentListing/types";
import TreatedOrNotTreatedTreatment from "src/components/modals/treatedOrNotTreatedTreatment";
import { downloadZipFile, exportToExcel } from "src/helpers/downloadFile";
import TreatmentDecision from "src/components/modals/treatmentDecision";
import { format, parse } from "date-fns";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { isIoveraUser, isZilrettaUser } from "src/helpers";
import ShowCopayModal from "src/components/modals/showCopayModal";
import useLocalStorage from "src/hooks/useUpdateLocalStorage";
import { UHCBanner } from "src/assets";


const Dashboard: FC = (): JSX.Element => {
  const [chartOptions, setChartOptions] = useState(BAR_CHART);
  const [queryParams, dispatch] = useReducer(queryParamReducer, initialState);
  const [showRequestTreatmentModal, setDataForRequestModalTreatment] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0

  });
  const [cancelTreatmentModal, setDataForCancelModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0
  });
  const [treatmentDecisionModal, setTreatmentDecisionModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number, prior_authorization_id: number, prior_auth_status: string | null, kase?: CaseListingType }>({

    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0,
    prior_authorization_id: 0,
    prior_auth_status: '',

  });
  const [treatedOrNotTreatedTreatmentModal, setDataForTreatedOrNotTreatedTreatmentModal] = useState<{ isModalVisible: boolean, caseId: number, treatment_id: number }>({
    isModalVisible: false,
    caseId: 0,
    treatment_id: 0
  });
  const [isExportingInProgress, setExportingInProgressStatus] = useState<boolean>(false);

  const { showToast } = useContext(ToastContext);
  const { userData, destroySession } = useContext(AuthContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const navigate = useNavigate();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const isZilrettaUserMemoized = useMemo(() => isZilrettaUser(userData), [userData])
  const isIOveraUserMemoized = useMemo(() => isIoveraUser(userData), [userData])
  const importantNoticeModalShown = useLocalStorage("importantNoticeModalShown");

  const [selectedCaseStatus, setSelectedCaseStatus] = useState<string>("All Cases");
  const [caseTableLisitng, updateCaseTableListing] = useState<CaseListAPIResponseType>()
  const [showModal, setShowModal] = useState(false);



  const { data: caseObject, mutate: getCaseListing, error: caseError, removeErrorAndData: clearCaseState } =
    useFetch<CaseListAPIResponseType, QueryParamsState>(APIEndpoints.CASE_LIST, queryParams, {}, false);

  useLayoutEffect(() => {
    setLoader()
    const initialQueryParams = {...queryParams};
    if (userData) {
      if (isZilrettaUserMemoized) {
        dispatch({ type: QueryStateActions.CHANGE_DRUG, payload: 'zilretta' })
        initialQueryParams.drug_name = 'zilretta'
      } else if (isIOveraUserMemoized) {
        dispatch({ type: QueryStateActions.CHANGE_DRUG, payload: 'iovera°' })
        initialQueryParams.drug_name = 'iovera°'
      }
    }

    getCaseListing({}, {}, initialQueryParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    if (caseError) {
      hideLoader();
      const { code, error } = caseError as unknown as CaseListErrorResponse;
      showToast([error || `Can't fetch cases details right now. Please try again in a while.`], ToastTypes.ERROR);
      if (code === 401) {
        destroySession();
        navigate(`/users/sign_in`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseError]);

  useEffect(() => {
    if (caseObject && caseObject.status) {
      hideLoader();
      if (caseObject.status === 500) {
        showToast(['No cases found against provided filters'], ToastTypes.ERROR);
      }

      if (queryParams.pagination) {
        updateCaseTableListing(caseObject)
        const latestChartOptions = { ...chartOptions };
        latestChartOptions.series = [{
          type: 'bar',
          name: 'Value',
          dataLabels: {
            enabled: true,
          },
          data: [caseObject.pending, caseObject.investigated],
        }];

        setChartOptions(latestChartOptions);
        const chart = chartComponentRef.current?.chart;

        if (chart) {
          chart.redraw();
        }
      } else {
        dispatch({
          type: QueryStateActions.CHANGE_PAGINATION_BIT,
          payload: true,
        })
        exportToExcel(caseObject.cases, "Case Details Report")
        setExportingInProgressStatus(false)
      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseObject]);

  const onClearButtonClicked = () => {
    if (caseError || caseObject) clearCaseState();
    setSelectedCaseStatus("All Cases");
    dispatch({
      type: QueryStateActions.CLEAR_STATE,
      payload: userData && isZilrettaUserMemoized ? "zilretta" : (isIOveraUserMemoized ? "iovera°" : "")
    });
    getCaseListing(null, null, queryParams);
  };

  const handleSearchWithQueryParam = (updatedState?: QueryParamsState) => {
    if (caseError || caseObject) clearCaseState();
    setLoader();
    getCaseListing(null, null, updatedState ? updatedState : queryParams);
  };


  const downloadExcelFile = () => {
    setExportingInProgressStatus(true)
    dispatch({
      type: QueryStateActions.CHANGE_PAGINATION_BIT,
      payload: false,
    })
    let params = {...queryParams};
    params.date_from = params.date_from ? format(parse(params.date_from, "MM/dd/yyyy", new Date()), "yyyy-MM-dd") : ''
    params.date_to =params.date_to ? format(parse(params.date_to, "MM/dd/yyyy", new Date()), "yyyy-MM-dd") :''
    getCaseListing({}, {}, { ...params, pagination: false })
  }


  const changeIdOrder = (value: string) => {
    const order = queryParams.direction === 'desc' ? 'asc' : 'desc';

    const action: QueryStateActionType = {
      type: QueryStateActions.CHANGE_SORT_PARAMS,
      payload: {
        direction: queryParams.sort_by === value ? order : "desc",
        sort_by: value,
      },
    };
    dispatch(action);

    const updatedState = queryParamReducer(queryParams, action);
    handleSearchWithQueryParam(updatedState);
  };

  const changePageNumber = (page: { selected: number; }) => {
    const action: QueryStateActionType = {
      type: QueryStateActions.CHANGE_PAGE,
      payload: page.selected + 1,
    };
    dispatch(action);

    const updatedState = queryParamReducer(queryParams, action);
    handleSearchWithQueryParam(updatedState);
  };

  const { data: treatmentUpdateData, mutate: updatetreatment, error: treatmentUpdateError } =
    useFetch<TreatmentStatusUpdateApiResponse>(APIEndpoints.TREATMENT_STATUS_UPDATE, {}, {}, false);

  useEffect(() => {
    if (treatmentUpdateError) {
      hideLoader();
      const { code, error } = treatmentUpdateError as unknown as TreatmentListErrorResponse;
      showToast([error || `Treatment can't update now. Please try again in a while.`], ToastTypes.ERROR);
      if (code === 401) {
        destroySession();
        navigate(`/users/sign_in`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentUpdateError]);

  useEffect(() => {
    const hasUpdateData = treatmentUpdateData?.code;

    if (hasUpdateData || treatmentUpdateError) {
      if (hasUpdateData) {
        getCaseListing(null, null, initialState);
      }
      hideLoader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentUpdateError, treatmentUpdateData]);


  const onTreatedOrNotTreatedButtonClicked = (value: string, treatment_id: number) => {
    const requestBody = { status: 0 };

    if (value === "Not-Treated") {
      requestBody.status = 31;
    } else {
      requestBody.status = 22;
    }
    setLoader()
    updatetreatment(requestBody, { treatmentId: treatment_id });
  };



  const { data: downloadCaseData, loading: downloadCaseLoading, error: downloadCaseError, mutate: getDownloadLinkForZipFile, removeErrorAndData: clearZipFileData } =
    useFetch<DownloadZipResponseType>(APIEndpoints.DOWNLOAD_ZIP_FILE, {}, {}, false);

  useEffect(() => {
    if (downloadCaseData || downloadCaseError) {
      if(downloadCaseData){
        const { response,report_name } = downloadCaseData;
        downloadZipFile(response,report_name.replace(/.pdf/g, ".zip"));
      }
      hideLoader();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadCaseData, downloadCaseError]);

  const downloadFileByCaseId = (caseId: number) => {
    if (downloadCaseData || downloadCaseError) clearZipFileData();
    setLoader();
    getDownloadLinkForZipFile({}, { caseId });
  };

  useEffect(() => {
    const isShown = localStorage.getItem("loginModalShown");
    // Only show when flag is explicitly set to true
    if (!isIOveraUserMemoized) {
      if (isShown === "true" && importantNoticeModalShown === "false" && userData && userData.copay_remaining_days_count > 0) {
        setShowModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importantNoticeModalShown]);




    return (
        <>
            <div className="overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill" id="cases-page">
                <div className="uhc-banner border shadow-sm my-3 post-login">
                    <img className="img-fluid" src={UHCBanner} alt="UHCBanner"/>
                </div>
                <div className="bg-mist py-2">
                    <div className="container-fluid">
                        <AuthSubHeader/>
                    </div>
                </div>
                <div className="container-fluid pt-3 d-flex flex-column flex-fill">
                    <div data-tour="fourth-step">
                        {caseObject ? (
                            <HighchartsReact highcharts={HighCharts} options={chartOptions} immutable={true}
                                             ref={chartComponentRef}/>
                        ) : null}
                    </div>

                    <div className="my-3">
                        <div className="row align-items-center">
                            <div className="col-12 col-md-8">
                                <h2 className="m-0 page-heading">{selectedCaseStatus}</h2>
                                <p>
                                    The following is a list of all active and completed cases. Click on the Case ID to
                                    view details of the case. Yellow cases require user attention.
                                </p>
                            </div>
                            <div className="col-12 col-md-4 text-end mb-3 mb-md-0">
                                {isExportingInProgress ? (
                                    <div className="col">
                                        <p className="text-dark h5">
                                            <span className="spinner-border spinner-border-sm"
                                                  style={{width: 20, height: 20}} aria-hidden="true"></span>{' '}
                                            <i>Fetching Case Details Report</i>
                                        </p>
                                    </div>
                                ) : (
                                    <button type="button" className="btn btn-primary" onClick={downloadExcelFile}>
                                        Case Details Report
                                    </button>
                                )}
                            </div>
                        </div>
                        <TableFilters
                            dropdownData={CASE_STATUS_LISTING}
                            inputPlaceholder="Search by Patient, Physician, Case ID"
                            state={queryParams}
                            dispatch={dispatch}
                            handleSearchWithQueryParam={handleSearchWithQueryParam}
                            onClearButtonClicked={onClearButtonClicked}
                            onStatusChange={setSelectedCaseStatus}
                        />
                    </div>
                    <div className="card mb-3 d-flex flex-fill">
                        <div className="card-body p-0 d-flex flex-column">
                            <div className="table-responsive-sm table-responsive-md table-responsive-lg flex-fill">
                                <table className="table table-striped table-sm m-0" data-tour="second-step">
                                    <thead>
                                    <tr className="text-center">
                                        {CASE_HEADER_LISTING_IS_IOVERA_PRACTICE.map((tableHeader, index) => (
                                            <th key={index}
                                                onClick={tableHeader.value !== '' ? () => changeIdOrder(tableHeader.value) : undefined}>
                                                <span role={tableHeader.value === '' ? "textbox" : "button"}
                                                    className={tableHeader.value === '' ?
                                                        '' : ''
                                                    }
                                                >{tableHeader.label}
                                                  {queryParams.sort_by === tableHeader.value ? (
                                                    <i className={`bi ${queryParams.direction === 'desc' ? 'bi-caret-down-fill' : 'bi-caret-up-fill'}`}></i>
                                                  ) : null}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {caseTableLisitng && caseTableLisitng.cases && caseTableLisitng.cases.length > 0 ?
                                        caseTableLisitng.cases.map(kase => (
                                            <tr key={kase.id} className="align-middle text-center">
                                                <td className="case-td">
                                                    <Link
                                                        to={{pathname: `/cases/detail/${kase.id}`}}
                                                        data-bs-toggle="tooltip"
                                                        data-bs-placement="right"
                                                        title={`Patient ID: ${kase.patient ? kase.patient.external_id : ''}`}
                                                        className={`${kase.drug_name === "iovera°" ? 'text-iovera' : 'text-zilretta'} `}>
                                                        {kase.id}
                                                    </Link>
                                                </td>
                                                <td>{kase ? kase.created_at : ''}</td>
                                                <td >{kase && kase.patient ? kase.patient.firstlastname : ''}</td>
                                                <td>{kase ? kase.drug_name : ''}</td>
                                                <td>{kase ? kase.submitting_practice : ''}</td>
                                                <td>{kase ? kase.soc_practice : ''}</td>
                                                <td>{kase && kase.doctor ? (typeof kase.doctor === "string" ? kase.doctor : kase.doctor.firstlastname) : ''}</td>
                                                <td>{kase ? kase.referring_doctor : ''}</td>
                                                <td className="align-middle">
                                                    <span className={`badge background-color-${kase && kase.color_idx ? kase.color_idx : 1}`}>
                                                        {kase ? kase.status_name : ''}
                                                    </span>
                                                </td>
                                                <td>{kase ? kase.sp_status : ""}</td>
                                                <td>{kase ? kase.sp_substatus : ""}</td>
                                                <td>{kase ? kase.copay_status : ""}</td>
                                                <td>
                                                    <RenderNextStep
                                                        kase={kase}
                                                        showHideModalForTreatment={setDataForRequestModalTreatment}
                                                        setDataForTreatmentDecisionModal={setTreatmentDecisionModal}
                                                    />
                                                </td>
                                                <td className={"action-btns"}>
                                                  <OverlayTrigger
                                                    overlay={<Tooltip>Download</Tooltip>}
                                                  >
                                                    <button
                                                      type="button" className="btn btn-info btn-sm"
                                                      disabled={downloadCaseLoading} onClick={() => downloadFileByCaseId(kase.id)}
                                                    >
                                                      <i className="bi bi-file-earmark-arrow-down"></i>
                                                    </button>
                                                  </OverlayTrigger>
                                                    &nbsp;
                                                  <OverlayTrigger
                                                    overlay={<Tooltip>Re-Run</Tooltip>}
                                                  >
                                                    <Link className="btn btn-primary btn-sm" to={`/cases/${kase ? kase.id : 0}/clone`}>
                                                      <i className="bi bi-copy"></i>
                                                    </Link>
                                                  </OverlayTrigger>
                                                </td>
                                            </tr>
                                        )) : null
                                    }
                                    </tbody>
                                </table>
                            </div>

                            {caseTableLisitng && caseTableLisitng.total_pages > 1 ? (
                                <ul className="pagination-nav sticky-bottom bg-white m-0">
                                    {queryParams.page !== 1 ? (
                                        <li className="nxt-lst page-item">
                                            <button className="btn" onClick={() => changePageNumber({selected: 0})}>
                                                <i className="bi bi-chevron-double-left"></i> First
                                            </button>
                                        </li>
                                    ) : null}

                                    <li>
                                        <ReactPaginate
                                            nextLabel={<>Next <i className="bi bi-chevron-right"></i></>}
                                            onPageChange={changePageNumber}
                                            pageRangeDisplayed={3}
                                            marginPagesDisplayed={2}
                                            pageCount={caseTableLisitng.total_pages}
                                            previousLabel={<><i className="bi bi-chevron-left"></i> Previous</>}
                                            pageClassName="page-item"
                                            pageLinkClassName="page-link"
                                            previousClassName="page-item"
                                            previousLinkClassName="page-link"
                                            nextClassName="page-item"
                                            nextLinkClassName="page-link"
                                            breakLabel="..."
                                            breakClassName="page-item"
                                            breakLinkClassName="page-link"
                                            containerClassName="pagination"
                                            activeClassName="active"
                                            renderOnZeroPageCount={null}
                                            forcePage={queryParams.page - 1}
                                        />
                                    </li>

                                    {queryParams.page !== caseTableLisitng.total_pages ? (
                                        <li className={`nxt-lst page-item ${queryParams.page === caseTableLisitng.total_pages ? 'disabled' : ''}`}>
                                            <button className="btn" onClick={() => changePageNumber({selected: caseTableLisitng.total_pages - 1})}>
                                                Last <i className="bi bi-chevron-double-right"></i>
                                            </button>
                                        </li>
                                    ) : null}
                                </ul>
                            ) : null}
                        </div>

                        <RequestDateOfTreatment
                            isModalVisible={showRequestTreatmentModal.isModalVisible}
                            caseId={showRequestTreatmentModal.caseId}
                            prescriptionId={showRequestTreatmentModal.prescriptionId}
                            hideModal={() => {
                                setDataForRequestModalTreatment({
                                    isModalVisible: false,
                                    caseId: 0,
                                    prescriptionId: 0
                                });
                            }}
                            refreshListing={() => {
                                handleSearchWithQueryParam();
                            }}
                        />

                        <CancelTreatment
                            isModalVisible={cancelTreatmentModal.isModalVisible}
                            caseId={cancelTreatmentModal.caseId}
                            prescriptionId={cancelTreatmentModal.prescriptionId}
                            hideModal={() => {
                                setDataForCancelModal({
                                    isModalVisible: false,
                                    caseId: 0,
                                    prescriptionId: 0
                                });
                            }}
                            refreshListing={() => {
                                handleSearchWithQueryParam();
                            }}
                        />

                        <TreatedOrNotTreatedTreatment
                            isModalVisible={treatedOrNotTreatedTreatmentModal.isModalVisible}
                            dataTreatmentStates={treatedOrNotTreatedTreatmentModal}
                            onTreatedOrNotTreatedButtonClick={onTreatedOrNotTreatedButtonClicked}
                            hideModal={() => {
                                setDataForTreatedOrNotTreatedTreatmentModal({
                                    isModalVisible: false,
                                    caseId: 0,
                                    treatment_id: 0
                                });
                            }}
                            refreshListing={() => {
                                handleSearchWithQueryParam();
                            }}
                        />
                        <TreatmentDecision
                            isModalVisible={treatmentDecisionModal.isModalVisible}
                            caseId={treatmentDecisionModal.caseId}
                            prescriptionId={treatmentDecisionModal.prescriptionId}
                            kase={treatmentDecisionModal.kase}
                            //prior_authorization_id={treatmentDecisionModal.prior_authorization_id}
                            hideModal={() => {
                                setTreatmentDecisionModal({
                                    isModalVisible: false,
                                    caseId: 0,
                                    prescriptionId: 0,
                                    prior_authorization_id: 0,
                                    prior_auth_status: '',

                                });
                            }}
                            refreshListing={() => {
                                handleSearchWithQueryParam();
                            }}
                        />
                        <ShowCopayModal
                          show={showModal}
                          onHide={() => {
                            setShowModal(false);
                            localStorage.setItem("loginModalShown", "false");
                          }}
                        />
                    </div>
                </div>
                <FooterText footerType="black"/>
            </div>
        </>
    );
};

export default Dashboard;
